'use client';

import React, { ReactNode, ComponentPropsWithoutRef, useState } from 'react';
import dynamic from 'next/dynamic';
import NextImage, { ImageProps, StaticImageData } from 'next/image';
import Link from 'next/link';
import type { MDXComponents as MDXComponentsType } from 'mdx/types';

import Callout, { CalloutType } from './Callout';
import CodeBlock from '@/components/CodeBlock';
import InlineSidebar from './InlineSidebar';
import LightboxGallery from './LightboxGallery';
import ScreenshotGrid from './ScreenshotGrid'
import ZoomableImageGrid from './ZoomableImageGrid';
import MDXImage from './MDXImage';

const VideoLightbox = dynamic(() => import('./VideoLightbox'), { ssr: false });
const PresentationSlide = dynamic(() => import('./PresentationSlide'), { ssr: false });

/**
 * ID generator for headings
 */
const generateId = (children: ReactNode): string => {
  const getText = (node: ReactNode): string => {
    if (!node) return '';
    if (typeof node === 'string' || typeof node === 'number') return node.toString();
    if (Array.isArray(node)) return node.map(getText).join('');
    if (React.isValidElement(node)) return getText((node.props as any).children);
    return '';
  };
  return getText(children)
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};


/* --- MDX Components --- */
const MDXComponents: MDXComponentsType = {
  /* Headings */
  h1: (props: ComponentPropsWithoutRef<'h1'>) => (
    <h1
      id={generateId(props.children)}
      className="text-3xl md:text-4xl font-black mt-12 mb-6 scroll-mt-32 tracking-tight"
      {...props}
    />
  ),
  h2: (props: ComponentPropsWithoutRef<'h2'>) => (
    <h2
      id={generateId(props.children)}
      className="text-2xl md:text-3xl font-bold mt-10 mb-4 scroll-mt-32 tracking-tight border-b border-zinc-200 dark:border-zinc-800 pb-2"
      {...props}
    />
  ),
  h3: (props: ComponentPropsWithoutRef<'h3'>) => (
    <h3
      id={generateId(props.children)}
      className="text-xl md:text-2xl font-bold mt-8 mb-3 scroll-mt-32 tracking-tight"
      {...props}
    />
  ),

  /* Prose */
  p: (props: ComponentPropsWithoutRef<'p'>) => (
    <p className="mb-6 leading-7 text-zinc-700 dark:text-zinc-300" {...props} />
  ),
  a: ({ href: originalHref, ...props }: ComponentPropsWithoutRef<'a'>) => {
    const href = originalHref || '';
    const isInternal = href.startsWith('/') || href.startsWith('#');
    const classes =
      'font-medium text-indigo-600 dark:text-indigo-400 underline underline-offset-4 decoration-indigo-500/30 hover:decoration-indigo-500 transition-colors';

    if (isInternal) return <Link href={href} className={classes} {...(props as any)} />;
    return (
      <a href={href} className={classes} target="_blank" rel="noopener noreferrer" {...props} />
    );
  },
  blockquote: (props: ComponentPropsWithoutRef<'blockquote'>) => (
    <blockquote className="border-l-4 border-indigo-500 pl-6 italic text-zinc-700 dark:text-zinc-300 bg-zinc-50 dark:bg-zinc-900/50 rounded-r-xl" {...props} />
  ),
  ul: (props: ComponentPropsWithoutRef<'ul'>) => (
    <ul className="list-disc list-outside pl-5 my-6 space-y-3 text-zinc-700 dark:text-zinc-300" {...props} />
  ),
  ol: (props: ComponentPropsWithoutRef<'ol'>) => (
    <ol className="list-decimal list-outside pl-5 my-6 space-y-3 text-zinc-700 dark:text-zinc-300" {...props} />
  ),

  LightboxGallery: (props: any) => <LightboxGallery {...props} />,
  ScreenshotGrid: (props: any) => <ScreenshotGrid {...props} />,
  ZoomableImageGrid: (props: any) => <ZoomableImageGrid {...props} />,

  /* Media & Code */
  Image: (props: any) => (
    <MDXImage 
      aspectRatio="video" // Default to 16:9 filling
      {...props} 
    />
  ),
  VideoLightbox: (props: any) => <VideoLightbox {...props} />,
  PresentationSlide: ({ children, ...rest }: any) => <PresentationSlide {...rest}>{children}</PresentationSlide>,

  code: (props: ComponentPropsWithoutRef<'code'>) => (
    <code className="bg-zinc-100 dark:bg-zinc-800 px-1.5 py-0.5 rounded-md text-[0.85em] font-mono text-indigo-600 dark:text-indigo-300 border border-zinc-200 dark:border-zinc-700/50" {...props} />
  ),
  pre: ({ children }: { children: ReactNode }) => {
    if (React.isValidElement(children)) {
      const childProps = children.props as { children?: ReactNode; className?: string };
      if (childProps && childProps.children && typeof childProps.children === 'string') {
        const language = childProps.className?.replace('language-', '') || 'plaintext';
        return (
          <div className="my-8">
            <CodeBlock language={language}>{childProps.children}</CodeBlock>
          </div>
        );
      }
    }
    return (
      <pre className="rounded-2xl border border-zinc-200 dark:border-zinc-800 overflow-hidden my-8 shadow-2xl p-4 bg-zinc-950 text-zinc-300">
        {children}
      </pre>
    );
  },

  /* Custom Content Blocks */
  Callout: (props: { type?: CalloutType; title?: string; children: ReactNode }) => (
    <div className="my-8"><Callout {...props} /></div>
  ),
  InlineSidebar: (props: { title?: string; children: ReactNode }) => <InlineSidebar {...props} />,
};

export default MDXComponents;
